import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, X, MessageCircle, ArrowRight } from 'lucide-react';
import { useT } from '../i18n';
import { useAuthUser } from '../lib/auth';

/**
 * Urgency sales alert — appears once per session after a short delay.
 * Hands the conversation to Rachel (WhatsApp concierge) or the free audit.
 * Hidden on internal areas and for signed-in users (admins/affiliates).
 */
export default function SalesAlert() {
  const { lang } = useT();
  const es = lang === 'es';
  const [show, setShow] = useState(false);
  const { pathname } = useLocation();
  const user = useAuthUser();
  const internal = pathname.startsWith('/admin') || pathname.startsWith('/affiliates/dashboard') || pathname === '/login';

  useEffect(() => {
    if (sessionStorage.getItem('kx_sales_alert_dismissed')) return;
    const mobile = window.innerWidth < 640;
    const t = setTimeout(() => setShow(true), 20000);
    let hide;
    if (mobile) hide = setTimeout(() => setShow(false), 40000);
    return () => { clearTimeout(t); clearTimeout(hide); };
  }, []);

  const close = () => {
    setShow(false);
    sessionStorage.setItem('kx_sales_alert_dismissed', '1');
  };

  if (internal || user) return null;

  const wa = `https://wa.me/14085901102?text=${encodeURIComponent(
    es
      ? 'Hola Rachel, necesito vender más. Quiero mi auditoría gratis 🚀'
      : 'Hi Rachel, I need to sell more. I want my free audit 🚀'
  )}`;

  return (
    <AnimatePresence>
      {show && (
        <motion.aside
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.97 }}
          transition={{ type: 'spring', stiffness: 260, damping: 24 }}
          className="fixed bottom-6 left-6 z-[90] w-[350px] max-w-[calc(100vw-3rem)] rounded-2xl bg-white shadow-[0_30px_90px_-20px_rgba(220,38,38,0.35)] ring-1 ring-red-200 overflow-hidden"
          role="status"
          data-testid="sales-alert"
        >
          <motion.div
            animate={{ opacity: [1, 0.5, 1] }}
            transition={{ duration: 1.6, repeat: Infinity }}
            className="h-1 bg-gradient-to-r from-red-600 via-red-500 to-[#F77F11]"
          />
          <div className="p-4.5 p-4">
            <div className="flex items-center justify-between gap-3">
              <span className="inline-flex items-center gap-1.5 text-red-600 text-[10px] font-bold tracking-[0.2em] uppercase">
                <AlertTriangle className="w-3.5 h-3.5" />
                {es ? 'Alerta de ventas · en vivo' : 'Sales alert · live'}
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-500" />
                </span>
              </span>
              <button
                onClick={close}
                aria-label="Close"
                className="w-7 h-7 rounded-full text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 flex items-center justify-center transition-colors"
                data-testid="sales-alert-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="mt-2.5 text-[14.5px] font-bold text-neutral-900 leading-snug">
              {es
                ? 'Tu competencia está capturando las búsquedas de tu zona ahora mismo.'
                : 'Your competitors are capturing your neighborhood searches right now.'}
            </p>
            <p className="mt-1.5 text-[12.5px] text-neutral-500 leading-relaxed">
              {es
                ? '8 de cada 10 comensales eligen entre los 3 primeros resultados. ¿Estás ahí?'
                : '8 in 10 diners pick from the top 3 results. Are you there?'}
            </p>
            <div className="mt-3.5 flex gap-2">
              <a
                href={wa}
                target="_blank"
                rel="noopener noreferrer"
                onClick={close}
                className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#25D366] hover:bg-[#1fb457] text-white h-10 rounded-full text-[13px] font-semibold transition-colors"
                data-testid="sales-alert-rachel"
              >
                <MessageCircle className="w-4 h-4" />
                {es ? 'Escríbele a Rachel' : 'Message Rachel'}
              </a>
              <Link
                to="/#audit"
                onClick={close}
                className="inline-flex items-center justify-center gap-1 border border-neutral-200 hover:border-[#F77F11] hover:text-[#F77F11] text-neutral-700 h-10 px-4 rounded-full text-[13px] font-semibold transition-colors"
                data-testid="sales-alert-audit"
              >
                {es ? 'Auditoría' : 'Audit'} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
